import React, { useState } from 'react';
import { Budget, Transaction } from '../types/budget';
import { TransactionFilters } from './TransactionFilters';
import { TransactionTable } from './TransactionTable';
import { TransactionDialog } from './TransactionDialog';
import { DeleteTransactionDialog } from './DeleteTransactionDialog';
import { useTransactions } from '../hooks/useTransactions';

interface TransactionListProps {
  budget: Budget;
  showAddButton?: boolean;
}

export const TransactionList: React.FC<TransactionListProps> = ({
  budget,
  showAddButton = true,
}) => {
  const {
    transactions,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    isFormOpen,
    setIsFormOpen,
    selectedTransaction,
    setSelectedTransaction,
    sortField,
    sortDirection,
    handleSort,
    handleAddTransaction,
    handleEditTransaction,
    handleDeleteTransaction,
  } = useTransactions({ budget });

  // State for inverse transaction
  const [inverseTransaction, setInverseTransaction] = useState<Transaction | undefined>(undefined);
  // State for transaction type
  const [transactionType, setTransactionType] = useState<'income' | 'expense' | undefined>(
    undefined
  );

  // State for delete confirmation dialog
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | undefined>(
    undefined
  );

  // Handle creating an inverse transaction
  const handleCreateInverse = (transaction: Transaction) => {
    setInverseTransaction(transaction);
    setTransactionType(transaction.amount < 0 ? 'expense' : 'income');
    setIsFormOpen(true);
  };

  // Handle edit transaction
  const handleEditClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTransactionType(transaction.amount >= 0 ? 'income' : 'expense');
  };

  // Handle delete transaction click
  const handleDeleteClick = (transaction: Transaction) => {
    setTransactionToDelete(transaction);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = () => {
    if (transactionToDelete) {
      handleDeleteTransaction(transactionToDelete);
    }
  };

  // Handle add transaction click
  const handleAddClick = (type: 'income' | 'expense') => {
    setTransactionType(type);
    setIsFormOpen(true);
  };

  return (
    <>
      <TransactionFilters
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        transactionCount={transactions.length}
        showAddButton={showAddButton}
        onAddClick={handleAddClick}
        categories={budget.categories}
        responsibles={budget.responsibles}
        destinations={budget.destinations}
      />

      <div className="-mx-4 overflow-x-auto sm:mx-0">
        <div className="inline-block min-w-full px-4 align-middle sm:px-0">
          <TransactionTable
            budget={budget}
            transactions={transactions}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCreateInverse={handleCreateInverse}
          />
        </div>
      </div>

      <TransactionDialog
        isOpen={isFormOpen || !!selectedTransaction}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedTransaction(undefined);
          setInverseTransaction(undefined);
          setTransactionType(undefined);
        }}
        selectedTransaction={selectedTransaction || inverseTransaction}
        isInverseTransaction={!!inverseTransaction && !selectedTransaction}
        onSubmit={selectedTransaction ? handleEditTransaction : handleAddTransaction}
        budgetId={budget.id || null}
        categories={budget.categories}
        responsibles={budget.responsibles}
        destinations={budget.destinations}
        transactionType={transactionType}
      />

      <DeleteTransactionDialog
        isOpen={!!transactionToDelete}
        onClose={() => setTransactionToDelete(undefined)}
        onConfirm={handleConfirmDelete}
        transaction={transactionToDelete}
        budget={budget}
      />
    </>
  );
};
